/** @format */

import moment, { Moment } from "moment";
import { THomeRichiesta } from "../../../redux-modules/declarations/maps/home";
import { periodoDa, TPeriodo, TTipoPeriodo } from "../ViewPresenze/partials/DaGestire";
import { maiuscola, orario } from "../ViewPresenze/format";

export {
  durataDiscorsiva,
  iniziali,
  maiuscola,
  minutiTra,
  nomeCompleto,
  normalizza,
  ora,
  orario,
  plurale,
  tinta,
  TRATTINO,
} from "../ViewPresenze/format";

/**
 * Testi e formati della Home, senza interfaccia. Gli stati (turni, persone, segnalazioni)
 * arrivano dal backend come nella vista Presenze: qui si sceglie solo come mostrarli.
 */

/** Data e ora del browser nel formato delle risposte, "YYYY-MM-DD HH:mm". */
export const adessoLocale = (m: Moment = moment()): string => m.format("YYYY-MM-DD HH:mm");

export const saluto = (adesso: Moment): string => {
  const h = adesso.hours();
  if (h < 13) return "Buongiorno";
  if (h < 18) return "Buon pomeriggio";
  return "Buonasera";
};

/** "Lunedì 19 ottobre 2026" */
export const dataLunga = (data: string): string =>
  maiuscola(moment(data).format("dddd D MMMM YYYY"));

/** "1° gennaio", "16 ottobre" */
export const giornoMese = (data: string): string => {
  const m = moment(data);
  return `${m.date() === 1 ? "1°" : m.date()} ${m.format("MMMM")}`;
};

/** "oggi", "domani", "ieri", altrimenti "martedì 20 ottobre" (anno solo se diverso). */
export const giornoRelativo = (data: string, oggi: string): string => {
  const giorni = moment(data).diff(moment(oggi), "days");
  if (giorni === 0) return "oggi";
  if (giorni === 1) return "domani";
  if (giorni === -1) return "ieri";
  const m = moment(data);
  return m.format(m.year() === moment(oggi).year() ? "dddd D MMMM" : "dddd D MMMM YYYY");
};

/** "oggi", "domani", altrimenti "gio 22" */
export const giornoBreveRelativo = (data: string, oggi: string): string => {
  const giorni = moment(data).diff(moment(oggi), "days");
  if (giorni === 0) return "oggi";
  if (giorni === 1) return "domani";
  return moment(data).format("ddd D");
};

/** "oggi alle 09:12", "ieri alle 18:00", "14 ottobre" */
export const momentoPassato = (dataOra: string, oggi: string): string => {
  const giorno = dataOra.substr(0, 10);
  const giorni = moment(oggi).diff(moment(giorno), "days");
  if (giorni === 0) return `oggi alle ${dataOra.substr(11, 5)}`;
  if (giorni === 1) return `ieri alle ${dataOra.substr(11, 5)}`;
  const m = moment(giorno);
  return m.format(m.year() === moment(oggi).year() ? "D MMMM" : "D MMMM YYYY");
};

/** Periodo di una richiesta: "mercoledì 21 ottobre", "dal 2 al 3 novembre", "venerdì 23 ottobre, 16:00–18:00". */
export const periodoRichiesta = (
  r: Pick<THomeRichiesta, "dal" | "al" | "unita">,
  oggi: string,
): string => {
  const da = moment(r.dal, "YYYY-MM-DD HH:mm");
  const a = moment(r.al, "YYYY-MM-DD HH:mm");
  const annoDiverso = da.year() !== moment(oggi).year();
  const giorno = (m: Moment, conSettimana = true): string =>
    m.format(`${conSettimana ? "dddd " : ""}D MMMM${annoDiverso ? " YYYY" : ""}`);
  if (da.isSame(a, "day")) {
    if (r.unita === "ORE") return `${giorno(da)}, ${orario(da.format("HH:mm"), a.format("HH:mm"))}`;
    return giorno(da);
  }
  if (r.unita === "ORE") {
    return `dal ${giorno(da, false)} alle ${da.format("HH:mm")} al ${giorno(
      a,
      false,
    )} alle ${a.format("HH:mm")}`;
  }
  if (da.isSame(a, "month")) return `dal ${da.date()} al ${giorno(a, false)}`;
  return `dal ${giorno(da, false)} al ${giorno(a, false)}`;
};

/** Pillola dello stato di una richiesta (stati di justificative_statuses). */
export const pillolaStatoRichiesta = (
  r: Pick<THomeRichiesta, "stato_id" | "stato">,
): { testo: string; classe: string } => {
  switch (r.stato_id) {
    case 1:
      return { testo: r.stato || "Accettata", classe: "verde" };
    case 2:
      return { testo: r.stato || "Validata", classe: "blu" };
    case 3:
      return { testo: r.stato || "In attesa", classe: "attesa" };
    case 4:
      return { testo: r.stato || "Rifiutata", classe: "rosso" };
    default:
      return { testo: r.stato || "—", classe: "grigio" };
  }
};

/** Scadenza di un documento: scaduto, in scadenza nei 7 giorni, oppure niente da segnalare. */
export const scadenzaDocumento = (
  scadenza: string | null,
  oggi: string,
): { testo: string; classe: string } | null => {
  if (!scadenza) return null;
  const giorni = moment(scadenza).diff(moment(oggi), "days");
  const quando = giornoMese(scadenza);
  if (giorni < 0) return { testo: `Scaduto il ${quando}`, classe: "rosso" };
  if (giorni <= 7)
    return { testo: giorni === 0 ? "Scade oggi" : `Scade il ${quando}`, classe: "ambra" };
  return null;
};

// ------------------------------------------------------------------ periodo di "Da gestire"

const CHIAVE_PRESENZE = "hr.presenze";
const TIPI_PERIODO: TTipoPeriodo[] = ["oggi", "ieri", "sette", "mese"];

/**
 * Periodo dei conteggi "Da gestire": lo stesso che la vista Presenze usa per la sua scheda
 * (preferenza salvata nel browser, di default gli ultimi 7 giorni), così i numeri coincidono.
 */
export const periodoDaGestire = (oggi: Moment): TPeriodo => {
  let tipo: TTipoPeriodo = "sette";
  try {
    const salvate = JSON.parse(window.localStorage.getItem(CHIAVE_PRESENZE) || "{}");
    if (TIPI_PERIODO.indexOf(salvate.periodo) >= 0) tipo = salvate.periodo;
  } catch (e) {
    // archiviazione non disponibile: ultimi 7 giorni
  }
  return periodoDa(tipo, oggi);
};

export const etichettaPeriodo = (tipo: TTipoPeriodo): string => {
  switch (tipo) {
    case "oggi":
      return "oggi";
    case "ieri":
      return "ieri";
    case "mese":
      return "questo mese";
    default:
      return "ultimi 7 giorni";
  }
};

// ------------------------------------------------------------------ preferenze della Home

const CHIAVE_HOME = "hr.home";

export interface TPreferenzeHome {
  /** gruppo scelto in "Chi c'è": id, oppure "tutti" */
  gruppo: string;
  /** reparti chiusi in "Chi c'è" */
  chiusi: string[];
}

export const leggiPreferenzeHome = (): TPreferenzeHome => {
  try {
    const salvate = JSON.parse(window.localStorage.getItem(CHIAVE_HOME) || "{}");
    return {
      gruppo: typeof salvate.gruppo === "string" ? salvate.gruppo : "tutti",
      chiusi: Array.isArray(salvate.chiusi)
        ? salvate.chiusi.filter((c: unknown) => typeof c === "string")
        : [],
    };
  } catch (e) {
    return { gruppo: "tutti", chiusi: [] };
  }
};

/** Salva solo le preferenze passate, lasciando le altre come sono. */
export const salvaPreferenzeHome = (preferenze: Partial<TPreferenzeHome>): void => {
  try {
    window.localStorage.setItem(
      CHIAVE_HOME,
      JSON.stringify({ ...leggiPreferenzeHome(), ...preferenze }),
    );
  } catch (e) {
    // archiviazione non disponibile: la scelta vale solo per questa visita
  }
};
